import { parseSeriesTimestamp } from './episodeDetection';
import { formatDurationMinutes } from './durationFormat';

export const MINUTE_MS = 60000;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;

const TICK_STEPS = [
  15 * MINUTE_MS,
  30 * MINUTE_MS,
  HOUR_MS,
  2 * HOUR_MS,
  3 * HOUR_MS,
  6 * HOUR_MS,
  12 * HOUR_MS,
  DAY_MS,
  2 * DAY_MS,
  7 * DAY_MS,
  14 * DAY_MS,
  30 * DAY_MS,
];

const START_PATTERNS = [/^inicio$/i, /^start$/i, /^start_?(time|at)?$/i, /inicio/i, /desde/i, /start/i];
const END_PATTERNS = [/^fin$/i, /^end$/i, /^end_?(time|at)?$/i, /(^|_)fin($|_)/i, /hasta/i, /end/i];

export const EMPTY_SERIES_LABEL = '(sin dato)';

function findRowKey(row, column) {
  if (!row || column == null || column === '') return null;
  if (Object.prototype.hasOwnProperty.call(row, column)) return column;
  const lower = String(column).toLowerCase();
  return Object.keys(row).find((k) => k.toLowerCase() === lower) || null;
}

export function intervalRowValue(row, column) {
  const key = findRowKey(row, column);
  return key ? row[key] : undefined;
}

function guessColumn(keys, patterns, exclude = []) {
  for (const re of patterns) {
    const hit = keys.find((k) => !exclude.includes(k) && re.test(k));
    if (hit) return hit;
  }
  return '';
}

/** Columnas efectivas de inicio/fin/serie; si no están configuradas, intenta inferir `inicio`/`fin`. */
export function resolveIntervalColumns(rows, config = {}) {
  const sample = Array.isArray(rows) ? rows.find((r) => r && typeof r === 'object') : null;
  const keys = sample ? Object.keys(sample) : [];
  const exists = (col) => Boolean(col) && (!sample || Boolean(findRowKey(sample, col)));
  const startColumn = exists(config.startColumn) ? config.startColumn : guessColumn(keys, START_PATTERNS);
  const endColumn = exists(config.endColumn)
    ? config.endColumn
    : guessColumn(keys, END_PATTERNS, [startColumn]);
  const seriesColumn = exists(config.seriesColumn) ? config.seriesColumn : '';
  return { startColumn, endColumn, seriesColumn };
}

/**
 * Intervalos + carriles: cada serie ocupa al menos un carril; si dos intervalos de la
 * misma serie se solapan, el segundo baja a un carril nuevo de esa serie.
 */
export function buildIntervalLayout(rows, { startColumn, endColumn, seriesColumn } = {}) {
  const items = [];
  const series = [];
  if (!Array.isArray(rows) || !startColumn || !endColumn) {
    return { items, lanes: [], series, min: null, max: null };
  }
  rows.forEach((row, index) => {
    const start = parseSeriesTimestamp(intervalRowValue(row, startColumn));
    const end = parseSeriesTimestamp(intervalRowValue(row, endColumn));
    if (start == null || end == null || end < start) return;
    let serie = '';
    if (seriesColumn) {
      const raw = intervalRowValue(row, seriesColumn);
      serie = raw == null || String(raw).trim() === '' ? EMPTY_SERIES_LABEL : String(raw);
    }
    if (!series.includes(serie)) series.push(serie);
    items.push({ row, index, start, end, serie });
  });

  const lanes = [];
  series.forEach((serie) => {
    const laneEnds = [];
    items
      .filter((it) => it.serie === serie)
      .sort((a, b) => a.start - b.start || a.end - b.end)
      .forEach((it) => {
        let lane = laneEnds.findIndex((end) => end <= it.start);
        if (lane === -1) {
          lane = laneEnds.length;
          laneEnds.push(it.end);
          lanes.push({ serie, lane });
        } else {
          laneEnds[lane] = it.end;
        }
        it.laneIndex = lanes.findIndex((l) => l.serie === serie && l.lane === lane);
      });
  });

  const min = items.length ? Math.min(...items.map((it) => it.start)) : null;
  const max = items.length ? Math.max(...items.map((it) => it.end)) : null;
  return { items, lanes, series, min, max };
}

/** Marcas "redondas" del eje de tiempo para el rango visible (se recalcula con el zoom). */
export function buildTimeTicks(min, max, maxTicks = 8) {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return { ticks: [], step: HOUR_MS };
  const span = max - min;
  const limit = Math.max(2, maxTicks);
  let step = TICK_STEPS.find((s) => span / s <= limit);
  if (!step) step = Math.ceil(span / limit / (30 * DAY_MS)) * 30 * DAY_MS;
  const ticks = [];
  for (let t = Math.ceil(min / step) * step; t <= max; t += step) ticks.push(t);
  return { ticks, step };
}

const pad2 = (n) => String(n).padStart(2, '0');

function dayLabel(ms) {
  const d = new Date(ms);
  return `${pad2(d.getUTCDate())}/${pad2(d.getUTCMonth() + 1)}`;
}

function clockLabel(ms) {
  const d = new Date(ms);
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

/** Etiqueta de marca: horas dentro del día y la fecha al cruzar la medianoche. */
export function formatTimeTick(value, step, isFirst = false) {
  if (step >= DAY_MS) return dayLabel(value);
  if (isFirst || value % DAY_MS === 0) return [clockLabel(value), dayLabel(value)];
  return clockLabel(value);
}

export function formatIntervalDateTime(ms) {
  const d = new Date(ms);
  return `${pad2(d.getUTCDate())}/${pad2(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${clockLabel(ms)}`;
}

export function formatIntervalDuration(startMs, endMs) {
  return formatDurationMinutes(Math.max(0, Math.round((endMs - startMs) / MINUTE_MS)), 'auto');
}
